import { clsx } from 'clsx';
import { PropsWithChildren, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { FocusScope } from 'react-aria';
import LoadingScreen from '~/components/LoadingScreen';
import IconClose from '~/icons/icon-close.svg?react';
import styles from './BookkeepingAside.module.scss';

export enum BookkeepingAsideState {
  CLOSED = 'closed',
  OPEN = 'open',
  UNMOUNTED = 'unmounted',
}

interface BookkeepingAsideProps extends PropsWithChildren {
  asideState: BookkeepingAsideState;
  desiredWidth: number;
  minWidth: number;
  maxWidthOffset?: number;
  setDesiredWidth: (width: number) => void;
  closeAside: () => void;
}

/**
 * Renders a resizable aside panel for bookkeeping-related asides.
 *
 * This aside component is different from a standard aside
 * in that it supports resizing.
 *
 * @category Component
 */
const BookkeepingAside: React.FC<BookkeepingAsideProps> = ({
  asideState,
  children,
  desiredWidth,
  minWidth,
  maxWidthOffset = 200,
  setDesiredWidth,
  closeAside,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);

  const constrainWidth = useCallback(
    (targetWidth: number) => {
      const maxWidth = Math.max(minWidth, windowWidth - maxWidthOffset);
      return Math.max(minWidth, Math.min(maxWidth, targetWidth));
    },
    [windowWidth, minWidth, maxWidthOffset],
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeAside();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [closeAside]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      startXRef.current = e.clientX;
      startWidthRef.current = desiredWidth;

      const handleMouseMove = (e: MouseEvent) => {
        const deltaX = startXRef.current - e.clientX;
        const newDesiredWidth = startWidthRef.current + deltaX;
        // Store the desired width (unconstrained) so it can be restored later
        setDesiredWidth(Math.max(minWidth, newDesiredWidth));
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [desiredWidth, minWidth, setDesiredWidth],
  );

  if (asideState === BookkeepingAsideState.UNMOUNTED) return null;

  const actualWidth = constrainWidth(desiredWidth);

  return (
    <>
      <div className={clsx(styles.container, styles[asideState])} style={{ width: actualWidth }}>
        <div className={styles.resizeHandle} onMouseDown={handleMouseDown} />
        <Suspense fallback={<LoadingScreen />}>
          <div className={styles.closeIconContainer} onClick={() => closeAside()}>
            <IconClose />
          </div>
          <FocusScope contain restoreFocus>
            {children}
          </FocusScope>
        </Suspense>
      </div>
      {isDragging && <div className={styles.dragOverlay} />}
    </>
  );
};

export default BookkeepingAside;
